/**
 * Carson OS 外壳。
 *
 * 结构上留位、内容上克制（PLAN §1）：三个页位现在都是空的，M1 先把「学业」填上——
 * 那是让 Carson 每天愿意打开这个 App 的钩子。壳收的 items 只有一份，
 * AppShell 在窄屏渲染成底部 TabBar、宽屏渲染成左侧 SideNav，业务页两边共用同一个组件。
 *
 * ⚠️ 外壳必须能离线打开：本文件到「已解锁 / 未解锁」为止都不依赖后端。
 * 断网时看到的是锁屏而不是白屏——白屏本身会被当成故障。
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { useOnline } from '@pwaship/react'
import {
  AppShell,
  Button,
  EmptyState,
  IconCheck,
  IconInfo,
  NavBar,
  OfflineBar,
  Screen,
} from '@pwaship/ui'

import SelfTestScreen from './screens/SelfTest'
import Academics from './screens/Academics'
import { api, session, type NavTab } from './lib/api'

export interface AppProps {
  path: string
  navigate(path: string): void
}

const ICONS: Record<string, ReactNode> = {
  academics: <IconCheck />,
  consumption: <IconCheck />,
  timeline: <IconInfo />,
  admin: <IconInfo />,
}

export default function App({ path, navigate }: AppProps) {
  // 能力自检场留在隐藏路由，别删（AGENTS.md 的要求，真机上只能靠它看能力是否活着）
  if (path.startsWith('/__selftest')) return <SelfTestScreen path={path} navigate={navigate} />

  return <Shell />
}

function Shell() {
  const online = useOnline()
  const [role, setRole] = useState<'carson' | 'neo' | null>(session.role)
  const [tabs, setTabs] = useState<NavTab[]>([])
  const [active, setActive] = useState('academics')

  const loadNav = useCallback(async () => {
    const r = await api.get<{ role: 'carson' | 'neo'; tabs: NavTab[] }>('/api/nav')
    if (r.ok) {
      setTabs(r.data.tabs)
      setActive((cur) => (r.data.tabs.some((t) => t.key === cur) ? cur : (r.data.tabs[0]?.key ?? cur)))
    } else if (r.kind === 'unauthorized') {
      setRole(null)
    }
  }, [])

  useEffect(() => { if (role) void loadNav() }, [role, loadNav])

  if (!role) return <Lock online={online} onUnlocked={setRole} />

  const items = tabs.map((t) => ({ key: t.key, label: t.label, icon: ICONS[t.key] ?? <IconInfo /> }))
  const current = tabs.find((t) => t.key === active)

  return (
    <AppShell
      items={items}
      active={active}
      onChange={setActive}
      brand={<strong style={{ fontSize: 17 }}>Carson OS</strong>}
      header={<NavBar title={current?.label ?? 'Carson OS'} />}
    >
      {!online && <OfflineBar />}
      {active === 'academics'
        ? <Academics role={role} />
        : <ComingSoon label={current?.label ?? ''} tabKey={active} />}
    </AppShell>
  )
}

/** 页位占位。M1 起逐个换成真页面，壳不需要跟着改。 */
function ComingSoon({ label, tabKey }: { label: string; tabKey: string }) {
  const next: Record<string, string> = {
    academics: 'M1 会把 AP / SAT 的目标、进度和考期倒数放在这里',
    consumption: 'M2 会把本月消费进度、分类和建议放在这里',
    timeline: 'M4 会把学业、消费、阅读的痕迹合成一条时间线',
    admin: 'M4 会把推送内容和完成度放在这里',
  }
  return (
    <EmptyState
      title={`${label}还没做`}
      description={next[tabKey] ?? '这个页位留着，后面会填上'}
    />
  )
}

function Lock({ online, onUnlocked }: { online: boolean; onUnlocked(r: 'carson' | 'neo'): void }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password || busy) return
    setBusy(true)
    setError(null)
    const r = await api.unlock(password)
    setBusy(false)
    if (r.ok) {
      setPassword('')
      onUnlocked(r.data.role)
    } else {
      setError(r.message)
      setPassword('')
    }
  }

  return (
    <Screen>
      {!online && <OfflineBar />}
      <form onSubmit={submit} style={{ display: 'grid', gap: 16, padding: 24, maxWidth: 360, margin: '18vh auto 0' }}>
        <div>
          <h1 style={{ margin: '0 0 6px', fontSize: 24 }}>Carson OS</h1>
          <p style={{ margin: 0, color: 'var(--ps-text-dim)', fontSize: 14 }}>输入密码即可进入，不需要用户名。</p>
        </div>
        <input
          type="password"
          value={password}
          autoFocus
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          aria-label="访问密码"
          style={{
            padding: '12px 14px', fontSize: 16, borderRadius: 10,
            border: '1px solid var(--ps-line)', background: 'var(--ps-surface)', color: 'var(--ps-text)',
          }}
        />
        {error && <p role="alert" style={{ margin: 0, color: 'var(--ps-danger)', fontSize: 14 }}>{error}</p>}
        <Button type="submit" disabled={!password || busy}>{busy ? '正在解锁…' : '解锁'}</Button>
      </form>
    </Screen>
  )
}
